import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { SubSink } from 'subsink';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { fromEvent, of } from 'rxjs';
import { debounceTime, map, switchAll, tap } from 'rxjs/operators';
import { SmallDialogComponent } from '../../../../atoms/small-dialog/small-dialog.component';
import { stringsList } from '../../../../constants/app-config';
import { TranslateService } from '@ngx-translate/core';
import { OrganizationalUnitDataService, OrganizationalUnitModel } from '../../services/organizational-unit-data.service';
import { OrganizationalUnitsAddDialogComponent } from '../organizational-units-add-dialog/organizational-units-add-dialog.component';

@Component({
  selector: 'app-organizational-units-table',
  templateUrl: './organizational-units-table.component.html',
  styleUrls: ['./organizational-units-table.component.css'],
})
export class OrganizationalUnitsTableComponent implements OnInit, AfterViewInit, OnDestroy {
  displayedColumns: string[] = ['checkbox', 'code', 'name', 'actions'];
  selectedInvoicesNum: number = 0;
  private subs = new SubSink();
  dataSource!: MatTableDataSource<OrganizationalUnitModel>;
  searchInput: string = '';
  isLoading = false;
  isSearchFilterOn = false;
  stringsList = stringsList;
  isHovered = false;
  isHovered1 = false;
  isAdditionalDataBeingFetched = false;
  userID: string | null = null;
  private savedScrollPosition = 0;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('scrollableContainer') scrollableContainer!: ElementRef<HTMLElement>;

  constructor(
    public ouDataService: OrganizationalUnitDataService,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    public translate: TranslateService,
    private input: ElementRef<HTMLInputElement>
  ) {
    const obs = fromEvent(this.input.nativeElement, 'keyup').pipe(
      map((e: any) => e.target.value),
      debounceTime(250),
      tap(() => { this.isSearchFilterOn = true; }),
      map((searchInput: string) => of(searchInput?.toLowerCase())),
      switchAll()
    );
    obs.subscribe(async (data) => {
      if (data.length) {
        await this.ouDataService.filterUnits(this.userID as string, data).catch(console.error);
      } else {
        await this.ouDataService.initializeData(this.userID as string).catch(console.error);
        this.isSearchFilterOn = false;
      }
    });
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.userID = localStorage.getItem('userID');
    this.ouDataService.initializeData(this.userID as string).then(() => {
      this.subs.sink = this.ouDataService.units.subscribe((data) => {
        this.dataSource = new MatTableDataSource<OrganizationalUnitModel>(data);
        this.dataSource.sort = this.sort;
        this.isLoading = false;
      });
    });
  }

  ngAfterViewInit(): void {
    if (this.dataSource) this.dataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  async onScroll(e: any) {
    if (this.isSearchFilterOn) return;
    const container = this.scrollableContainer.nativeElement;
    const remaining = container.scrollHeight - (container.scrollTop + container.clientHeight);
    this.savedScrollPosition = container.scrollTop;
    if (remaining < container.clientHeight * 0.9 && !this.isAdditionalDataBeingFetched) {
      this.isAdditionalDataBeingFetched = true;
      await this.ouDataService.fetchAdditional(this.userID as string).then(() => {
        this.isAdditionalDataBeingFetched = false;
      });
    }
  }

  addNewUnit(): void {
    const dialogRef = this.dialog.open(OrganizationalUnitsAddDialogComponent, {
      width: '420px',
      data: { action: 'Add' },
    });
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.snackBar.open('Organizational unit successfully added', '', {
          duration: 5000,
          panelClass: ['white-snackbar'],
        });
      }
    });
  }

  editUnit(unit: OrganizationalUnitModel): void {
    this.dialog.open(OrganizationalUnitsAddDialogComponent, {
      width: '420px',
      data: { action: 'Edit', data: { ...unit } },
    });
  }

  toggleCheckbox(unit: OrganizationalUnitModel) {
    if (unit.selected) {
      this.selectedInvoicesNum++;
    } else {
      this.selectedInvoicesNum--;
    }
  }

  toggleAllSelection(seed: boolean) {
    if (seed) {
      this.dataSource.data.forEach((obj) => { obj.selected = true; });
      this.selectedInvoicesNum = this.dataSource.data.length;
    } else {
      this.dataSource.data.forEach((obj) => { obj.selected = false; });
      this.selectedInvoicesNum = 0;
    }
  }

  async bulkDeleteUnits() {
    const unitsForDelete = this.dataSource.data.filter((a: any) => a.selected);
    const plural = unitsForDelete.length > 1;
    const messages = plural
      ? {
          heading: 'Delete units?',
          contentPartOne: 'Are you sure you want to delete these organizational units?',
          action: 'Delete units',
        }
      : {
          heading: 'Delete unit?',
          contentPartOne: 'Are you sure you want to delete this organizational unit?',
          action: 'Delete unit',
        };

    const dialogRef = this.dialog.open(SmallDialogComponent, {
      width: '360px',
      data: messages,
    });

    dialogRef.afterClosed().subscribe(async (res) => {
      if (res) {
        for (const unit of unitsForDelete) {
          await this.ouDataService.removeUnit(unit).catch(console.error);
        }
        this.toggleAllSelection(false);
      }
    });
  }

  deleteUnit(unit: OrganizationalUnitModel): void {
    const dialogRef = this.dialog.open(SmallDialogComponent, {
      width: '360px',
      data: {
        heading: 'Delete unit?',
        contentPartOne: `Are you sure you want to delete "${unit.name}"?`,
        action: 'Delete unit',
      },
    });
    dialogRef.afterClosed().subscribe(async (res) => {
      if (res) {
        await this.ouDataService.removeUnit(unit).catch(console.error);
      }
    });
  }
}
